/**
 * AI Assistant Tab
 * Chat interface for AI-powered CRM assistance
 * Phase 9 Implementation - Enhanced with Actionable Intents
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Drawer,
  useTheme,
  useMediaQuery,
  Alert,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Add as AddIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  TipsAndUpdates as TipsIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  AssignmentInd as AssignIcon,
  TaskAlt as TaskIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aiAssistantApi,
  ChatMessage,
  ConversationSummary,
  ConversationHistory,
  ActionableIntent,
  LeadPreview,
  TeamMember
} from '../api/aiAssistantApi';
import { showToast } from '../utils/toast';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  actionableIntent?: ActionableIntent | null;
}

// Helper function to detect if a message is asking for confirmation
const isConfirmationPrompt = (content: string): boolean => {
  const lowerContent = content.toLowerCase();
  const confirmationPhrases = [
    'shall i proceed',
    'would you like me to proceed',
    'do you want me to',
    'should i proceed',
    'want me to assign',
    'want me to create',
    'confirm this action',
    'ready to assign',
    'ready to create'
  ];
  return confirmationPhrases.some(phrase => lowerContent.includes(phrase));
};

const AIAssistantTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [pendingAction, setPendingAction] = useState<ActionableIntent | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');

  // Fetch conversations
  const { data: conversations = [], isLoading: conversationsLoading } = useQuery({
    queryKey: ['ai-conversations'],
    queryFn: () => aiAssistantApi.getConversations()
  });

  // Fetch team members for assignment
  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => aiAssistantApi.getTeamMembers()
  });

  // Chat mutation
  const chatMutation = useMutation({
    mutationFn: (message: string) =>
      aiAssistantApi.chat({
        message,
        conversationId: currentConversationId || undefined
      }),
    onSuccess: (response: ChatMessage) => {
      // Update conversation ID if it's a new conversation
      if (!currentConversationId) {
        setCurrentConversationId(response.conversationId);
      }

      // Add assistant response
      const assistantMessage: Message = {
        id: response.id,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(response.createdAt),
        suggestions: response.suggestions,
        actionableIntent: response.actionableIntent
      };
      setMessages(prev => [...prev, assistantMessage]);

      // If there's an actionable intent, set it as pending
      if (response.actionableIntent?.requiresConfirmation) {
        setPendingAction(response.actionableIntent);
        // Pre-select assignee if provided
        if (response.actionableIntent.params.assigneeId) {
          setSelectedAssignee(response.actionableIntent.params.assigneeId);
        }
      }

      // Refresh conversations list
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });

      // If an action was executed (response contains success message), refresh data
      if (response.intent === 'action_executed' || response.response.includes('✅')) {
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['lead'] }); // For single lead detail
      }
    },
    onError: () => {
      showToast.operationFailed('Failed to send message');
      // Remove the pending user message
      setMessages(prev => prev.slice(0, -1));
    }
  });

  // Confirm action mutation
  const confirmActionMutation = useMutation({
    mutationFn: (params: { actionType: string; params: Record<string, any> }) =>
      aiAssistantApi.confirmAction(params),
    onSuccess: (result) => {
      showToast.success(result.message || 'Action completed successfully');

      // Add confirmation message
      const confirmMessage: Message = {
        id: `confirm-${Date.now()}`,
        role: 'assistant',
        content: `${result.message}`,
        timestamp: new Date(),
        suggestions: ['Show my leads', 'What else needs attention?', 'Create another task']
      };
      setMessages(prev => [...prev, confirmMessage]);

      // Clear pending action
      setPendingAction(null);
      setSelectedAssignee('');

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error: any) => {
      showToast.operationFailed(error.message || 'Failed to execute action');
    }
  });

  // Load conversation history
  const loadConversationMutation = useMutation({
    mutationFn: (conversationId: string) =>
      aiAssistantApi.getConversationHistory(conversationId),
    onSuccess: (data: ConversationHistory) => {
      const loadedMessages: Message[] = [];
      data.messages.forEach(msg => {
        loadedMessages.push({
          id: `user-${msg.id}`,
          role: 'user',
          content: msg.userMessage,
          timestamp: new Date(msg.timestamp)
        });
        loadedMessages.push({
          id: `assistant-${msg.id}`,
          role: 'assistant',
          content: msg.aiResponse,
          timestamp: new Date(msg.timestamp)
        });
      });
      setMessages(loadedMessages);
      setCurrentConversationId(data.conversationId);
      setPendingAction(null);
    }
  });

  // Delete conversation
  const deleteMutation = useMutation({
    mutationFn: (conversationId: string) =>
      aiAssistantApi.deleteConversation(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
      if (currentConversationId) {
        startNewConversation();
      }
      showToast.success('Conversation deleted');
    }
  });

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingAction]);

  // Welcome message on mount
  useEffect(() => {
    if (messages.length === 0 && !currentConversationId) {
      const welcomeMessage: Message = {
        id: 'welcome',
        role: 'assistant',
        content: `Hello! I'm your AI CRM Assistant. I can help you with:\n\n- **Lead Management**: Track, prioritize, and assign leads\n- **Task Management**: Create and assign tasks\n- **Client Insights**: Monitor relationships\n- **Sales Support**: Get recommendations\n\n**Try saying:**\n- "Assign my high priority leads to John"\n- "Create a follow-up task for tomorrow"\n- "Show leads that need attention"`,
        timestamp: new Date(),
        suggestions: ['Show my leads', 'Assign unassigned leads', 'Create a task', 'What needs attention?']
      };
      setMessages([welcomeMessage]);
    }
  }, []);

  const handleSendMessage = () => {
    if (!inputValue.trim() || chatMutation.isPending) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    chatMutation.mutate(inputValue.trim());
    setInputValue('');
    setPendingAction(null); // Clear any pending action when new message sent
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  // Handle quick confirmation (Yes/No buttons)
  const handleQuickConfirmation = (confirmed: boolean) => {
    const responseText = confirmed ? 'Yes, proceed' : 'No, cancel';

    // Add user message to show what they clicked
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: responseText,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Send the confirmation to the backend
    chatMutation.mutate(responseText);
  };

  const handleConfirmAction = () => {
    if (!pendingAction) return;

    const params = { ...pendingAction.params };

    // If assignee needs to be selected
    if ((pendingAction.actionType === 'ASSIGN_LEADS' || pendingAction.actionType === 'ASSIGN_TASKS') && !params.assigneeId) {
      if (!selectedAssignee) {
        showToast.error('Please select a team member to assign to');
        return;
      }
      params.assigneeId = selectedAssignee;
    }

    confirmActionMutation.mutate({
      actionType: pendingAction.actionType,
      params
    });
  };

  const handleCancelAction = () => {
    setPendingAction(null);
    setSelectedAssignee('');

    // Add cancel message
    const cancelMessage: Message = {
      id: `cancel-${Date.now()}`,
      role: 'assistant',
      content: 'Action cancelled. How else can I help you?',
      timestamp: new Date(),
      suggestions: ['Show my leads', 'Create a task', 'What needs attention?']
    };
    setMessages(prev => [...prev, cancelMessage]);
  };

  const startNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setPendingAction(null);
    setSelectedAssignee('');
    // Re-add welcome message
    const welcomeMessage: Message = {
      id: 'welcome',
      role: 'assistant',
      content: `Hello! I'm your AI CRM Assistant. I can help you with:\n\n- **Lead Management**: Track, prioritize, and assign leads\n- **Task Management**: Create and assign tasks\n- **Client Insights**: Monitor relationships\n- **Sales Support**: Get recommendations\n\n**Try saying:**\n- "Assign my high priority leads to John"\n- "Create a follow-up task for tomorrow"\n- "Show leads that need attention"`,
      timestamp: new Date(),
      suggestions: ['Show my leads', 'Assign unassigned leads', 'Create a task', 'What needs attention?']
    };
    setMessages([welcomeMessage]);
    if (isMobile) setSidebarOpen(false);
  };

  const loadConversation = (conversationId: string) => {
    loadConversationMutation.mutate(conversationId);
    if (isMobile) setSidebarOpen(false);
  };

  const handleDeleteConversation = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    if (window.confirm('Delete this conversation?')) {
      deleteMutation.mutate(conversationId);
    }
  };

  // Render action confirmation card
  const renderActionConfirmation = () => {
    if (!pendingAction) return null;

    const isLeadAssign = pendingAction.actionType === 'ASSIGN_LEADS';
    const isTaskAssign = pendingAction.actionType === 'ASSIGN_TASKS';
    const isTaskCreate = pendingAction.actionType === 'CREATE_TASK';
    const needsAssigneeSelection = (isLeadAssign || isTaskAssign) && !pendingAction.params.assigneeId;

    return (
      <Card sx={{ mx: 2, mb: 2, border: '2px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {isLeadAssign && <AssignIcon color="primary" />}
            {isTaskCreate && <TaskIcon color="primary" />}
            {isTaskAssign && <AssignIcon color="primary" />}
            <Typography variant="subtitle1" fontWeight="bold">
              {isLeadAssign && 'Assign Leads'}
              {isTaskCreate && 'Create Task'}
              {isTaskAssign && 'Assign Tasks'}
            </Typography>
          </Box>

          {/* Preview Data - Leads */}
          {isLeadAssign && pendingAction.previewData && (
            <TableContainer sx={{ mb: 2, maxHeight: 200 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Contact</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(pendingAction.previewData as LeadPreview[]).map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>{lead.contactName}</TableCell>
                      <TableCell>{lead.companyName}</TableCell>
                      <TableCell>
                        <Chip
                          label={lead.priority}
                          size="small"
                          color={lead.priority === 'HIGH' || lead.priority === 'URGENT' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{lead.aiScore || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Task Create Preview */}
          {isTaskCreate && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Title:</strong> {pendingAction.params.title}</Typography>
              <Typography variant="body2"><strong>Type:</strong> {pendingAction.params.taskType}</Typography>
              <Typography variant="body2"><strong>Due:</strong> {pendingAction.params.dueDate ? new Date(pendingAction.params.dueDate).toLocaleDateString() : 'N/A'}</Typography>
              <Typography variant="body2"><strong>Priority:</strong> {pendingAction.params.priority}</Typography>
            </Box>
          )}

          {/* Assignee Selection */}
          {needsAssigneeSelection && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assign To</InputLabel>
              <Select
                value={selectedAssignee}
                label="Assign To"
                onChange={(e) => setSelectedAssignee(e.target.value)}
              >
                {teamMembers.map((member: TeamMember) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name} ({member.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Pre-selected Assignee */}
          {pendingAction.params.assigneeName && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Assigning to:</strong> {pendingAction.params.assigneeName}
            </Typography>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={confirmActionMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
              onClick={handleConfirmAction}
              disabled={confirmActionMutation.isPending || (needsAssigneeSelection && !selectedAssignee)}
            >
              {confirmActionMutation.isPending ? 'Processing...' : 'Confirm'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancelAction}
              disabled={confirmActionMutation.isPending}
            >
              Cancel
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Sidebar content
  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* New Chat Button */}
      <Box sx={{ p: 2 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<AddIcon />}
          onClick={startNewConversation}
          sx={{ textTransform: 'none' }}
        >
          New Conversation
        </Button>
      </Box>

      <Divider />

      {/* Conversation List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          <HistoryIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
          Recent Conversations
        </Typography>

        {conversationsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : conversations.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
            No conversations yet
          </Typography>
        ) : (
          <List dense>
            {conversations.map((conv: ConversationSummary) => (
              <ListItem
                key={conv.conversationId}
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => handleDeleteConversation(e, conv.conversationId)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemButton
                  selected={currentConversationId === conv.conversationId}
                  onClick={() => loadConversation(conv.conversationId)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ChatIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={conv.title}
                    secondary={new Date(conv.createdAt).toLocaleDateString()}
                    primaryTypographyProps={{
                      noWrap: true,
                      variant: 'body2'
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Tips */}
      <Divider />
      <Box sx={{ p: 2 }}>
        <Alert severity="info" icon={<TipsIcon />} sx={{ '& .MuiAlert-message': { fontSize: 12 } }}>
          <strong>Tip:</strong> Try "Assign high priority leads to [name]" or "Create a follow-up task"
        </Alert>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 300px)', minHeight: 500 }}>
      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            width: 280,
            borderRight: 1,
            borderColor: 'divider',
            display: sidebarOpen ? 'block' : 'none'
          }}
        >
          {sidebarContent}
        </Paper>
      )}

      {/* Sidebar - Mobile Drawer */}
      {isMobile && (
        <Drawer
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: 280 } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton onClick={() => setSidebarOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {sidebarContent}
        </Drawer>
      )}

      {/* Main Chat Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Chat Header */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          {isMobile && (
            <IconButton onClick={() => setSidebarOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <BotIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              AI CRM Assistant
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {chatMutation.isPending ? 'Thinking...' : confirmActionMutation.isPending ? 'Executing action...' : 'Online'}
            </Typography>
          </Box>
        </Paper>

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            bgcolor: 'grey.50'
          }}
        >
          {loadConversationMutation.isPending && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                gap: 1
              }}
            >
              {message.role === 'assistant' && (
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                  <BotIcon fontSize="small" />
                </Avatar>
              )}

              <Box sx={{ maxWidth: '70%' }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    borderRadius: 2,
                    borderTopLeftRadius: message.role === 'assistant' ? 0 : 16,
                    borderTopRightRadius: message.role === 'user' ? 0 : 16
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      '& strong': { fontWeight: 600 }
                    }}
                    dangerouslySetInnerHTML={{
                      __html: message.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n/g, '<br/>')
                    }}
                  />
                </Paper>

                {/* Yes/No Confirmation Buttons - Only show on the last assistant message that asks for confirmation */}
                {message.role === 'assistant' &&
                  messages[messages.length - 1]?.id === message.id &&
                  isConfirmationPrompt(message.content) &&
                  !chatMutation.isPending && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckIcon />}
                      onClick={() => handleQuickConfirmation(true)}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Yes, proceed
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={() => handleQuickConfirmation(false)}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      No, cancel
                    </Button>
                  </Box>
                )}

                {/* Suggestions */}
                {message.role === 'assistant' && message.suggestions && message.suggestions.length > 0 && !pendingAction && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {message.suggestions.map((suggestion, index) => (
                      <Chip
                        key={index}
                        label={suggestion}
                        size="small"
                        variant="outlined"
                        onClick={() => handleSuggestionClick(suggestion)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' } }}
                      />
                    ))}
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>

              {message.role === 'user' && (
                <Avatar sx={{ bgcolor: 'grey.400', width: 32, height: 32 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
              )}
            </Box>
          ))}

          {/* Action Confirmation Card */}
          {renderActionConfirmation()}

          {/* Typing indicator */}
          {chatMutation.isPending && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <BotIcon fontSize="small" />
              </Avatar>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2, borderTopLeftRadius: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Box className="typing-dot" sx={{ width: 8, height: 8, bgcolor: 'grey.400', borderRadius: '50%', animation: 'typing 1s infinite' }} />
                  <Box className="typing-dot" sx={{ width: 8, height: 8, bgcolor: 'grey.400', borderRadius: '50%', animation: 'typing 1s infinite 0.2s' }} />
                  <Box className="typing-dot" sx={{ width: 8, height: 8, bgcolor: 'grey.400', borderRadius: '50%', animation: 'typing 1s infinite 0.4s' }} />
                </Box>
              </Paper>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 1
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder={pendingAction ? "Confirm or cancel the action above..." : "Type your message..."}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={chatMutation.isPending || confirmActionMutation.isPending}
            size="small"
            multiline
            maxRows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || chatMutation.isPending || confirmActionMutation.isPending}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: 'grey.300' }
            }}
          >
            {chatMutation.isPending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Paper>
      </Box>

      {/* CSS Animation for typing dots */}
      <style>
        {`
          @keyframes typing {
            0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
            30% { transform: translateY(-4px); opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
};

export default AIAssistantTab;
